// The desktop hero (1024px and up): the constellation sphere, right of the
// window collage, with the hero copy (headline, subhead and See how it works)
// centred under it; Get started lives in the nav. The pair is one stack, set a little above the
// middle of the room between the nav and the provider rail.
//
// When that room is short the sphere gives way first, down to the size its
// centre mark (logo and name) needs; then the copy sheds the subhead, never the
// headline or See how it works. On a stage too short even for the headline and that sphere, the
// sphere shrinks further. The copy never reaches the provider rail.
//
// The copy's parts are measured whether or not they show (a shed subhead is
// laid out collapsed), so which lines fit depends only on the stage and the
// measured text, never on what is showing: the choice can't flip back and forth.
//
// Everything is in px of the stage: the viewport above the consent bar, left
// unrounded, so the CSS form below lands on exactly the same positions.
//
// Until the window and the copy are measured (the server render, and a visitor
// without JavaScript), the copy is placed by DESKTOP_HERO_PROVISIONAL_COPY_CSS:
// the same layout written in CSS, for English text and the whole viewport, so
// the headline paints at once and, in English without the consent bar, the
// measured layout takes over without moving it.

/** All of the copy, or the headline and See how it works. */
export type DesktopHeroCopySize = "desktop" | "desktop-headline";

/** Each part's rendered height, with the space above it; the action is See how it works. */
export type DesktopHeroCopyParts = { headline: number; subhead: number; action: number };

export type DesktopHeroLayoutInput = {
  width: number;
  height: number;
  /** The copy's parts as measured; null until they have rendered. */
  copyParts: DesktopHeroCopyParts | null;
};

export type DesktopHeroLayout = {
  /** The sphere's centre and size; x is a share of the stage width. */
  orb: { xShare: number; y: number; diameter: number };
  /** The copy block, centred on the sphere's x. */
  copy: { top: number; width: number; height: number; size: DesktopHeroCopySize };
};

/** The sphere sits right of the collage, at this share of the stage width. */
export const DESKTOP_HERO_ORB_X_SHARE = 0.69;
/**
 * The collage's widest point, as a share of the stage width: the furthest
 * right its reveal reaches (DESKTOP_VISIBLE_CLIP_PATH, x 442 of 1000).
 */
export const DESKTOP_HERO_COLLAGE_EDGE_SHARE = 0.442;
/** The fixed nav row ends here. */
export const DESKTOP_HERO_NAV_BOTTOM = 78;
/** The footer: the provider rail and the legal links (sm:h-24). */
export const DESKTOP_HERO_FOOTER_HEIGHT = 96;
/**
 * English at the widest copy column, until the copy has rendered: what
 * useHeroCopyHeight measures (each part rounded up to whole px), so the
 * server-rendered placement and the measured one agree.
 */
export const DESKTOP_HERO_COPY_FALLBACK_PARTS: DesktopHeroCopyParts = { headline: 38, subhead: 60, action: 66 };

const NAV_GAP = 18;
const FOOTER_GAP = 16;
const ORB_COPY_GAP = 28;
// The sphere's centre mark (logo and name) needs this much sphere around it.
const MIN_ORB = 150;
// Only a stage too short for the headline and MIN_ORB goes below it.
const SMALLEST_ORB = 64;
const MAX_ORB = 340;
// This share of the spare height goes above the stack, the rest below it.
const LEAD_SHARE = 0.42;
const COPY_MAX_WIDTH = 600;
const COPY_EDGE_MARGIN = 32;
const COLLAGE_CLEARANCE = 24;

const CUTS: ReadonlyArray<readonly [DesktopHeroCopySize, (parts: DesktopHeroCopyParts) => number]> = [
  ["desktop", (parts) => parts.headline + parts.subhead + parts.action],
  ["desktop-headline", (parts) => parts.headline + parts.action],
];

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/** The copy column: as wide as reads well, clear of the screen edge and of the collage. */
export function getDesktopHeroCopyWidth(width: number): number {
  const centre = width * DESKTOP_HERO_ORB_X_SHARE;
  const roomBeside = 2 * (width - centre - COPY_EDGE_MARGIN);
  const roomPastCollage = 2 * (centre - width * DESKTOP_HERO_COLLAGE_EDGE_SHARE - COLLAGE_CLEARANCE);
  return Math.min(COPY_MAX_WIDTH, roomBeside, roomPastCollage);
}

export function getDesktopHeroLayout({ width, height, copyParts }: DesktopHeroLayoutInput): DesktopHeroLayout {
  const parts = copyParts ?? DESKTOP_HERO_COPY_FALLBACK_PARTS;
  const top = DESKTOP_HERO_NAV_BOTTOM + NAV_GAP;
  const bottom = height - DESKTOP_HERO_FOOTER_HEIGHT - FOOTER_GAP;
  const room = Math.max(0, bottom - top);

  // The fullest copy that leaves the sphere room for its centre mark.
  const fits = (copyHeight: number) => copyHeight + ORB_COPY_GAP + MIN_ORB <= room;
  const [size, heightOf] = CUTS.find(([, heightOf]) => fits(heightOf(parts))) ?? CUTS[CUTS.length - 1];
  const copyHeight = Math.ceil(heightOf(parts));

  // The sphere's preferred size grows with the stage, as it always has; the
  // copy's room comes first.
  const preferred = clamp(Math.min(width - 220, height - 280) * 0.45, 220, MAX_ORB);
  const orbRoom = room - ORB_COPY_GAP - copyHeight;
  const diameter = clamp(Math.min(preferred, orbRoom), clamp(orbRoom, SMALLEST_ORB, MIN_ORB), MAX_ORB);
  const stack = diameter + ORB_COPY_GAP + copyHeight;
  const leadTop = top + Math.max(0, room - stack) * LEAD_SHARE;
  // Never into the provider rail: on a stage too short for even the smallest
  // stack, it rises into the gap under the nav instead.
  const copyTop = Math.min(leadTop + diameter + ORB_COPY_GAP, bottom - copyHeight);
  const orbTop = copyTop - ORB_COPY_GAP - diameter;

  return {
    orb: { xShare: DESKTOP_HERO_ORB_X_SHARE, y: orbTop + diameter / 2, diameter },
    copy: { top: copyTop, width: getDesktopHeroCopyWidth(width), height: copyHeight, size },
  };
}

// The layout above in CSS, for the fallback parts and the whole viewport.
const STAGE_WIDTH = "100vw";
const LAYOUT_TOP = DESKTOP_HERO_NAV_BOTTOM + NAV_GAP;
const LAYOUT_BOTTOM_INSET = DESKTOP_HERO_FOOTER_HEIGHT + FOOTER_GAP;
const cssNumber = (value: number) => String(Number(value.toFixed(4)));

/** Mirrors getDesktopHeroCopyWidth. */
function copyWidthCss(): string {
  const beside = `${cssNumber(2 * (1 - DESKTOP_HERO_ORB_X_SHARE) * 100)}vw - ${2 * COPY_EDGE_MARGIN}px`;
  const pastCollage = `${cssNumber(2 * (DESKTOP_HERO_ORB_X_SHARE - DESKTOP_HERO_COLLAGE_EDGE_SHARE) * 100)}vw - ${2 * COLLAGE_CLEARANCE}px`;
  return `min(${COPY_MAX_WIDTH}px, ${beside}, ${pastCollage})`;
}

/** Mirrors getDesktopHeroLayout's copy top for a copy block this tall. */
function copyTopCss(copyHeight: number, stageHeight: string): string {
  const room = `max(0px, ${stageHeight} - ${LAYOUT_TOP + LAYOUT_BOTTOM_INSET}px)`;
  const preferred = `clamp(220px, min(${STAGE_WIDTH} - 220px, ${stageHeight} - 280px) * 0.45, ${MAX_ORB}px)`;
  const orbRoom = `(${room} - ${ORB_COPY_GAP + copyHeight}px)`;
  const diameter = `clamp(clamp(${SMALLEST_ORB}px, ${orbRoom}, ${MIN_ORB}px), min(${preferred}, ${orbRoom}), ${MAX_ORB}px)`;
  const leadTop = `${LAYOUT_TOP}px + max(0px, ${room} - ${diameter} - ${ORB_COPY_GAP + copyHeight}px) * ${LEAD_SHARE}`;
  return `min(${leadTop} + ${diameter} + ${ORB_COPY_GAP}px, ${stageHeight} - ${LAYOUT_BOTTOM_INSET + copyHeight}px)`;
}

/** The copy block marks itself with this until it is measured. */
export const HERO_COPY_PROVISIONAL_ATTRIBUTE = "data-hero-copy-provisional";
const PROVISIONAL = `[${HERO_COPY_PROVISIONAL_ATTRIBUTE}]`;

// Twice: in vh, then in dvh, which a browser without dvh drops. On desktop
// the two are the same height.
const placementRule = (copyHeight: number) =>
  `${PROVISIONAL}{top:${copyTopCss(copyHeight, "100vh")};top:${copyTopCss(copyHeight, "100dvh")};}`;

const SHED_LINES: Record<DesktopHeroCopySize, ReadonlyArray<keyof DesktopHeroCopyParts>> = {
  desktop: [],
  "desktop-headline": ["subhead"],
};

/**
 * The provisional copy placement: its width, its top, and, on a short
 * viewport, the subhead shed, at the height where the JS layout sheds it for
 * the fallback parts. Rules apply
 * only while the copy carries HERO_COPY_PROVISIONAL_ATTRIBUTE; the copy's
 * lines carry data-hero-copy-line.
 */
export const DESKTOP_HERO_PROVISIONAL_COPY_CSS = [
  `${PROVISIONAL}{width:${copyWidthCss()};}`,
  placementRule(Math.ceil(CUTS[0][1](DESKTOP_HERO_COPY_FALLBACK_PARTS))),
  ...CUTS.slice(0, -1).map(([size, heightOf], index) => {
    // The JS layout keeps this cut while it fits; below that, the next one.
    const fitsFrom = LAYOUT_TOP + LAYOUT_BOTTOM_INSET + ORB_COPY_GAP + MIN_ORB + Math.ceil(heightOf(DESKTOP_HERO_COPY_FALLBACK_PARTS));
    const [nextSize, nextHeightOf] = CUTS[index + 1];
    const shed = SHED_LINES[nextSize]
      .filter((line) => !SHED_LINES[size].includes(line))
      .map((line) => `${PROVISIONAL} [data-hero-copy-line=${line}]{height:0;overflow:hidden;visibility:hidden;}`)
      .join("");
    return `@media (max-height:${fitsFrom - 0.02}px){${placementRule(Math.ceil(nextHeightOf(DESKTOP_HERO_COPY_FALLBACK_PARTS)))}${shed}}`;
  }),
].join("\n");
